import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PMDashboardPage from './pages/dashboard/PMDashboardPage';
import TasksPage from './pages/tasks/TasksPage';
import TaskDetailPage from './pages/tasks/TaskDetailPage';
import WBSTreePage from './pages/wbs/WBSTreePage';
import WBSDetailPage from './pages/wbs/WBSDetailPage';
import BudgetPage from './pages/budget/BudgetPage';
import ReportsPage from './pages/reports/ReportsPage';
import QsWorkspacePage from './pages/qs/QsWorkspacePage';
import AdminPage from './pages/admin/AdminPage';
import ProcurementPage from './pages/procurement/ProcurementPage';
import PurchaseOrderDetailPage from './pages/procurement/PurchaseOrderDetailPage';
import StoresPage from './pages/stores/StoresPage';
import EquipmentPage from './pages/equipment/EquipmentPage';
import AccountsPage from './pages/accounts/AccountsPage';
import CalendarsAdminPage from './pages/calendars/CalendarsAdminPage';
import FinancialsPage from './pages/financials/FinancialsPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import DefectsPage from './pages/defects/DefectsPage';
import UnitStatusPage from './pages/unitStatus/UnitStatusPage';
import ResourcesPage from './pages/resources/ResourcesPage';
import RiskRegisterPage from './pages/risks/RiskRegisterPage';
import PermitsPage from './pages/permits/PermitsPage';
import InspectionsPage from './pages/inspections/InspectionsPage';
import QcTemplatesPage from './pages/quality/QcTemplatesPage';
import QcRunPage from './pages/quality/QcRunPage';
import ChangeRequestsPage from './pages/changeRequests/ChangeRequestsPage';
import ChangeRequestDetailPage from './pages/changeRequests/ChangeRequestDetailPage';
import HseRegisterPage from './pages/hse/HseRegisterPage';
import ContractAdminWorkspacePage from './pages/contractAdmin/ContractAdminWorkspacePage';
import MainLayout from './components/layout/MainLayout';
import FieldModeLayout from './components/layout/FieldModeLayout';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import FieldLandingPage from './pages/field/FieldLandingPage';
import SupervisorFieldModePage from './pages/field/SupervisorFieldModePage';
import QsFieldModePage from './pages/field/QsFieldModePage';
import PmFieldModePage from './pages/field/PmFieldModePage';
import ExecutiveFieldModePage from './pages/field/ExecutiveFieldModePage';
import AdminFieldModePage from './pages/field/AdminFieldModePage';
import SupervisorInspectionDetailPage from './pages/field/SupervisorInspectionDetailPage';
import SupervisorFieldReportPage from './pages/field/SupervisorFieldReportPage';
import SupervisorTimesheetPage from './pages/field/SupervisorTimesheetPage';
import SupervisorDispatchPage from './pages/field/SupervisorDispatchPage';
import AdminWorkerMonitorPage from './pages/field/AdminWorkerMonitorPage';
import AdminWorkerAccountViewPage from './pages/field/AdminWorkerAccountViewPage';
import WorkerFieldWorkspacePage from './pages/field/WorkerFieldWorkspacePage';
import AttendancePage from './pages/field/AttendancePage';
import DfrPage from './pages/field/DfrPage';
import TimesheetsPage from './pages/field/TimesheetsPage';
import SyncConflictsPage from './pages/sync/SyncConflictsPage';
import { routePaths } from './routes/routePaths';
import { canUseAdminFieldViewSwitcherForUser, changePasswordPathForRole, effectiveFieldRoleForUser, fieldLandingForRole, isFieldOnlyRole, normalizeRole } from './routes/fieldAccess';

// Protected Route wrapper
function ProtectedRoute({ children, allowFieldOnly = false }: { children: React.ReactNode; allowFieldOnly?: boolean }) {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const role = user?.role;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.requiresPasswordChange && !allowFieldOnly) {
    return <Navigate to={changePasswordPathForRole(role)} replace />;
  }

  if (!allowFieldOnly && isFieldOnlyRole(role)) {
    return <Navigate to="/field" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
}

const workerFieldRoles = ['construction', 'worker', 'construction_worker'];
const supervisorFieldRoles = ['supervisor'];
const commercialFieldRoles = ['accounts', 'procurement', 'stores'];
const accountsFieldRoles = ['accounts'];
const procurementFieldRoles = ['procurement'];
const storesFieldRoles = ['stores'];
const qsFieldRoles = ['qs'];
const pmFieldRoles = ['pm'];
const executiveFieldRoles = ['executive'];
const adminFieldRoles = ['field_admin'];
const allFieldRoles = [...workerFieldRoles, ...supervisorFieldRoles, ...commercialFieldRoles, ...qsFieldRoles, ...pmFieldRoles, ...executiveFieldRoles, ...adminFieldRoles];

function FieldProtectedRoute({
  children,
  allowedRoles,
  allowPasswordChange = false,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowPasswordChange?: boolean;
}) {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const role = user?.role;
  const normalizedRole = normalizeRole(role);
  const adminFieldViewer = canUseAdminFieldViewSwitcherForUser(normalizedRole, user?.email);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.requiresPasswordChange && !allowPasswordChange) {
    return <Navigate to={changePasswordPathForRole(role)} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(normalizedRole) && !adminFieldViewer) {
    return <Navigate to={isFieldOnlyRole(role) ? '/field' : routePaths.dashboard} replace />;
  }

  return <FieldModeLayout>{children}</FieldModeLayout>;
}

function FieldTimesheetRoute() {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const email = useSelector((state: RootState) => state.auth.user?.email);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fieldRole = effectiveFieldRoleForUser(role, searchParams.get('view'), location.pathname, email);
  return fieldRole === 'supervisor' ? <SupervisorTimesheetPage /> : <TimesheetsPage fieldMode />;
}

function DefaultRedirect() {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  return <Navigate to={isFieldOnlyRole(role) ? fieldLandingForRole(role) : routePaths.dashboard} replace />;
}

function CatchAllRedirect() {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={isFieldOnlyRole(role) ? '/field' : routePaths.dashboard} replace />;
}

function DashboardRoute() {
  const role = useSelector((state: RootState) => state.auth.user?.role);

  return isFieldOnlyRole(role) ? <Navigate to="/field" replace /> : <DashboardPage />;
}

function FieldEmergencyRoute() {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const email = useSelector((state: RootState) => state.auth.user?.email);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fieldRole = effectiveFieldRoleForUser(role, searchParams.get('view'), location.pathname, email);
  return fieldRole === 'supervisor'
    ? <SupervisorFieldModePage view="emergency" />
    : <WorkerFieldWorkspacePage view="emergency" />;
}

function FieldDefectsRoute() {
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const email = useSelector((state: RootState) => state.auth.user?.email);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fieldRole = effectiveFieldRoleForUser(role, searchParams.get('view'), location.pathname, email);
  return fieldRole === 'supervisor'
    ? <DefectsPage fieldMode />
    : <WorkerFieldWorkspacePage view="defects" />;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route
        path={routePaths.changePassword}
        element={
          <ProtectedRoute allowFieldOnly>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.fieldChangePassword}
        element={
          <FieldProtectedRoute allowPasswordChange>
            <ChangePasswordPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path={routePaths.dashboard}
        element={
          <ProtectedRoute>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.pmDashboard}
        element={
          <ProtectedRoute>
            <PMDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.dispatchBoard}
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route path="/dispatch" element={<Navigate to="/tasks" replace />} />
      <Route
        path={routePaths.field}
        element={
          <FieldProtectedRoute allowedRoles={allFieldRoles}>
            <FieldLandingPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/emergency"
        element={
          <FieldProtectedRoute allowedRoles={[...workerFieldRoles, ...supervisorFieldRoles]}>
            <FieldEmergencyRoute />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/worker/emergency"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <WorkerFieldWorkspacePage view="emergency" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/supervisor/current"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorFieldModePage view="current" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/supervisor/upcoming"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorFieldModePage view="upcoming" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/supervisor/qc"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorFieldModePage view="qc" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/purchase-orders/:purchaseOrderId"
        element={
          <FieldProtectedRoute allowedRoles={commercialFieldRoles}>
            <PurchaseOrderDetailPage fieldMode />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/accounts/purchase-orders"
        element={
          <FieldProtectedRoute allowedRoles={accountsFieldRoles}>
            <AccountsPage fieldMode view="purchase-orders" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/accounts/construction-tasks"
        element={
          <FieldProtectedRoute allowedRoles={accountsFieldRoles}>
            <AccountsPage fieldMode view="construction-tasks" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/accounts/other-transactions"
        element={
          <FieldProtectedRoute allowedRoles={accountsFieldRoles}>
            <AccountsPage fieldMode view="other-transactions" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/accounts/transactions"
        element={
          <FieldProtectedRoute allowedRoles={accountsFieldRoles}>
            <AccountsPage fieldMode view="transactions" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/accounts/drawdown"
        element={
          <FieldProtectedRoute allowedRoles={accountsFieldRoles}>
            <AccountsPage fieldMode view="drawdown" />
          </FieldProtectedRoute>
        }
      />
      {/* Legacy accounts destinations: the localStorage-backed admin payroll view was
          removed (no backend model) and history folded into the transactions register. */}
      <Route path="/field/accounts/admin" element={<Navigate to="/field/accounts/transactions" replace />} />
      <Route path="/field/accounts/history" element={<Navigate to="/field/accounts/transactions" replace />} />
      <Route
        path="/field/qs/drawdown"
        element={
          <FieldProtectedRoute allowedRoles={qsFieldRoles}>
            <QsFieldModePage view="drawdown" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/qs/previous-drawdowns"
        element={
          <FieldProtectedRoute allowedRoles={qsFieldRoles}>
            <QsFieldModePage view="previous-drawdowns" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/pm/lookahead"
        element={
          <FieldProtectedRoute allowedRoles={pmFieldRoles}>
            <PmFieldModePage view="lookahead" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/pm/dispatch"
        element={
          <FieldProtectedRoute allowedRoles={pmFieldRoles}>
            <PmFieldModePage view="dispatch" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/pm/issued"
        element={
          <FieldProtectedRoute allowedRoles={pmFieldRoles}>
            <PmFieldModePage view="issued" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/pm/submitted-reports"
        element={
          <FieldProtectedRoute allowedRoles={pmFieldRoles}>
            <PmFieldModePage view="submitted-reports" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/pm/reviews"
        element={
          <FieldProtectedRoute allowedRoles={pmFieldRoles}>
            <PmFieldModePage view="reviews" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/notifications"
        element={
          <FieldProtectedRoute allowedRoles={allFieldRoles}>
            <NotificationsPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/admin/history"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminFieldModePage view="history" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/estate-progress"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Estate progress could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="estate-progress" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/construction-tasks"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Executive construction tasks could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="construction-tasks" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/supervisor-inspections"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Executive supervisor inspections could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="supervisor-inspections" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/stores"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Executive stores analytics could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="stores" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/procurement"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Executive procurement analytics could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="procurement" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/executive/financial"
        element={
          <FieldProtectedRoute allowedRoles={executiveFieldRoles}>
            <RouteErrorBoundary message="Executive financials could not be displayed. Reload to get the latest app version.">
              <ExecutiveFieldModePage view="financial" />
            </RouteErrorBoundary>
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/admin/users"
        element={
          <FieldProtectedRoute allowedRoles={adminFieldRoles}>
            <AdminFieldModePage view="users" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/admin/teams"
        element={
          <FieldProtectedRoute allowedRoles={adminFieldRoles}>
            <AdminFieldModePage view="teams" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/admin/field-mode"
        element={
          <FieldProtectedRoute allowedRoles={adminFieldRoles}>
            <AdminFieldModePage view="field-mode" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/procurement/orders"
        element={
          <FieldProtectedRoute allowedRoles={procurementFieldRoles}>
            <ProcurementPage fieldMode view="pos" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/procurement/at-risk"
        element={
          <FieldProtectedRoute allowedRoles={procurementFieldRoles}>
            <ProcurementPage fieldMode view="at-risk" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/procurement/suppliers"
        element={
          <FieldProtectedRoute allowedRoles={procurementFieldRoles}>
            <ProcurementPage fieldMode view="suppliers" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/stores/receiving"
        element={
          <FieldProtectedRoute allowedRoles={storesFieldRoles}>
            <StoresPage fieldMode view="receiving" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/stores/picking"
        element={
          <FieldProtectedRoute allowedRoles={storesFieldRoles}>
            <StoresPage fieldMode view="picking" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/stores/stock"
        element={
          <FieldProtectedRoute allowedRoles={storesFieldRoles}>
            <StoresPage fieldMode view="stock" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/inspections/:taskId"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorInspectionDetailPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerMonitorPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers/:workerId"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerAccountViewPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers/:workerId/current"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerAccountViewPage screen="current" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers/:workerId/upcoming"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerAccountViewPage screen="upcoming" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers/:workerId/performance"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerAccountViewPage screen="performance" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/workers/:workerId/emergency"
        element={
          <FieldProtectedRoute allowedRoles={['admin']}>
            <AdminWorkerAccountViewPage screen="emergency" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/tasks"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <WorkerFieldWorkspacePage view="current" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/upcoming"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <WorkerFieldWorkspacePage view="upcoming" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/performance"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <WorkerFieldWorkspacePage view="performance" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/tasks/:id"
        element={
          <FieldProtectedRoute allowedRoles={[...workerFieldRoles, ...supervisorFieldRoles, ...pmFieldRoles, ...adminFieldRoles]}>
            <TaskDetailPage fieldMode />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/dfr"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorFieldReportPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/dispatch"
        element={
          <FieldProtectedRoute allowedRoles={supervisorFieldRoles}>
            <SupervisorDispatchPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/timesheets"
        element={
          <FieldProtectedRoute allowedRoles={allFieldRoles}>
            <FieldTimesheetRoute />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/attendance"
        element={
          <FieldProtectedRoute allowedRoles={[...workerFieldRoles, ...supervisorFieldRoles]}>
            <AttendancePage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/qc-templates"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <QcTemplatesPage />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/qc-runs/:id"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <QcRunPage fieldMode />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/defects"
        element={
          <FieldProtectedRoute allowedRoles={[...workerFieldRoles, ...supervisorFieldRoles]}>
            <FieldDefectsRoute />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/field/site-inspections"
        element={
          <FieldProtectedRoute allowedRoles={workerFieldRoles}>
            <WorkerFieldWorkspacePage view="inspections" />
          </FieldProtectedRoute>
        }
      />
      <Route
        path="/tasks/:id"
        element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.wbs}
        element={
          <ProtectedRoute>
            <WBSTreePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wbs/:id"
        element={
          <ProtectedRoute>
            <WBSDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.budget}
        element={
          <ProtectedRoute>
            <BudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.reports}
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.qs}
        element={
          <ProtectedRoute>
            <QsWorkspacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.admin}
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.procurement}
        element={
          <ProtectedRoute>
            <ProcurementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.stores}
        element={
          <ProtectedRoute>
            <StoresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.accounts}
        element={
          <ProtectedRoute>
            <AccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/:purchaseOrderId"
        element={
          <ProtectedRoute>
            <PurchaseOrderDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.workCalendars}
        element={
          <ProtectedRoute>
            <CalendarsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.financials}
        element={
          <ProtectedRoute>
            <FinancialsPage />
          </ProtectedRoute>
        }
      />
      <Route path={routePaths.drawdowns} element={<Navigate to={`${routePaths.financials}?tab=drawdowns`} replace />} />
      <Route
        path={routePaths.documents}
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.notifications}
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.defects}
        element={
          <ProtectedRoute>
            <DefectsPage />
          </ProtectedRoute>
        }
      />
      <Route path={routePaths.defectAnalytics} element={<Navigate to={`${routePaths.defects}?tab=analytics`} replace />} />
      <Route path={routePaths.teamsOverview} element={<Navigate to={`${routePaths.admin}?tab=teams-overview`} replace />} />
      <Route
        path={routePaths.unitStatus}
        element={
          <ProtectedRoute>
            <UnitStatusPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.resources}
        element={
          <ProtectedRoute>
            <ResourcesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.equipment}
        element={
          <ProtectedRoute>
            <EquipmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.risks}
        element={
          <ProtectedRoute>
            <RiskRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.permits}
        element={
          <ProtectedRoute>
            <PermitsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.inspections}
        element={
          <ProtectedRoute>
            <InspectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.hse}
        element={
          <ProtectedRoute>
            <HseRegisterPage />
          </ProtectedRoute>
        }
      />
      {[
        routePaths.hseIncidents,
        routePaths.hseAccidents,
        routePaths.hseJsas,
        routePaths.hseHazards,
        routePaths.hsePpeChecks,
        routePaths.hseSafetyInspections,
        routePaths.hseEmergencyContacts,
        routePaths.hseFirstAidReadiness,
        routePaths.hseStandDowns,
      ].map((path) => (
        <Route key={path} path={path} element={<Navigate to={routePaths.hse} replace />} />
      ))}
      <Route
        path={routePaths.contractAdmin}
        element={
          <ProtectedRoute>
            <ContractAdminWorkspacePage />
          </ProtectedRoute>
        }
      />
      {[
        [routePaths.contractAdminContracts, 'contracts'],
        [routePaths.contractAdminSubcontracts, 'contracts'],
        [routePaths.contractAdminInstructions, 'instructions'],
        [routePaths.contractAdminRfis, 'instructions'],
        [routePaths.contractAdminVariations, 'variations'],
        [routePaths.contractAdminEot, 'variations'],
        [routePaths.contractAdminClaims, 'claims'],
        [routePaths.contractAdminContraCharges, 'claims'],
        [routePaths.contractAdminNotices, 'claims'],
        [routePaths.contractAdminPaymentApplications, 'payments'],
        [routePaths.contractAdminRetention, 'payments'],
        [routePaths.contractAdminFinalAccounts, 'payments'],
      ].map(([path, tab]) => (
        <Route key={path} path={path} element={<Navigate to={`${routePaths.contractAdmin}?tab=${tab}`} replace />} />
      ))}
      <Route
        path={routePaths.dfr}
        element={
          <ProtectedRoute>
            <DfrPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.timesheets}
        element={
          <ProtectedRoute>
            <TimesheetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.syncConflicts}
        element={
          <ProtectedRoute>
            <SyncConflictsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.qcTemplates}
        element={
          <ProtectedRoute>
            <QcTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qc-runs/:id"
        element={
          <ProtectedRoute>
            <QcRunPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={routePaths.changeRequests}
        element={
          <ProtectedRoute>
            <ChangeRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/change-requests/:id"
        element={
          <ProtectedRoute>
            <ChangeRequestDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
}

export default App;
